import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  OrganisationMemberResponse,
  EventTypeCategory,
} from '@/client/types.gen';
import { getOrganisationMembers } from '@/client/sdk.gen';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface EventTypeHostsSelectorProps {
  selectedHosts: string[];
  eventCategory: EventTypeCategory;
  onSelectedHostsChange: (hosts: string[]) => void;
  initialHostDetails?: OrganisationMemberResponse[];
}

export function EventTypeHostsSelector({
  selectedHosts = [],
  eventCategory,
  onSelectedHostsChange,
  initialHostDetails = [],
}: EventTypeHostsSelectorProps) {
  const { toast } = useToast();
  const [members, setMembers] =
    useState<OrganisationMemberResponse[]>(initialHostDetails);
  const [isLoadingMembers, setIsLoadingMembers] = useState(
    initialHostDetails.length === 0
  );
  const [openHostsSelector, setOpenHostsSelector] = useState(false);

  useEffect(() => {
    fetchOrganisationMembers();
  }, []);

  // Use initial host details if available
  useEffect(() => {
    if (initialHostDetails.length > 0 && members.length === 0) {
      console.log('Using initial host details:', initialHostDetails);
      setMembers((prev) => {
        // If we already have members, don't overwrite them
        if (prev.length > 0) return prev;
        return initialHostDetails;
      });
    }
  }, [initialHostDetails]);

  // Debug log for selected hosts
  useEffect(() => {
    console.log('Selected hosts updated:', selectedHosts);
  }, [selectedHosts]);

  // Fetch organisation members for host selection
  const fetchOrganisationMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const response = await getOrganisationMembers();
      if (response.data) {
        console.log('Fetched members:', response.data.members);

        // Merge with any initialHostDetails to avoid duplicates
        const newMembers = response.data.members;

        if (initialHostDetails.length > 0) {
          // Create a map of existing member IDs for quick lookup
          const existingMemberIds = new Set(
            initialHostDetails.map((m) => m.id)
          );

          // Add any new members that aren't in initialHostDetails
          const mergedMembers = [
            ...initialHostDetails,
            ...newMembers.filter((m) => !existingMemberIds.has(m.id)),
          ];

          setMembers(mergedMembers);
        } else {
          setMembers(newMembers);
        }
      }
    } catch (error) {
      console.error('Failed to fetch organisation members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Handle host selection
  const toggleHost = (memberId: string) => {
    if (eventCategory === 'ONE_ON_ONE' || eventCategory === 'GROUP') {
      // For one-on-one or group, only one host allowed
      onSelectedHostsChange([memberId]);
    } else {
      // For collective or round robin, multiple hosts allowed
      onSelectedHostsChange(
        selectedHosts.includes(memberId)
          ? selectedHosts.filter((id) => id !== memberId)
          : [...selectedHosts, memberId]
      );
    }
  };

  // Get member name by ID
  const getMemberNameById = (id: string) => {
    const member = members.find((m) => m.id === id);
    return member ? member.email : id;
  };

  // Format display text for selected hosts
  const getSelectedHostsDisplayText = () => {
    if (selectedHosts.length === 0) return 'Select hosts';

    if (selectedHosts.length === 1) {
      return getMemberNameById(selectedHosts[0]);
    }

    if (selectedHosts.length <= 2) {
      return selectedHosts.map((id) => getMemberNameById(id)).join(', ');
    }

    return `${getMemberNameById(selectedHosts[0])} +${
      selectedHosts.length - 1
    } more`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="hosts">Hosts</Label>
        {(eventCategory === 'COLLECTIVE' ||
          eventCategory === 'ROUND_ROBIN') && (
          <Badge variant="outline" className="font-normal">
            Multiple allowed
          </Badge>
        )}
        {(eventCategory === 'ONE_ON_ONE' || eventCategory === 'GROUP') && (
          <Badge variant="outline" className="font-normal">
            Single host
          </Badge>
        )}
      </div>

      <Popover open={openHostsSelector} onOpenChange={setOpenHostsSelector}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={openHostsSelector}
            className="w-full justify-between"
            disabled={isLoadingMembers}
          >
            {isLoadingMembers ? (
              <span className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading hosts...
              </span>
            ) : (
              <span className="truncate">{getSelectedHostsDisplayText()}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Search hosts..." />
            <CommandEmpty>No hosts found.</CommandEmpty>
            <CommandGroup>
              {members.map((member) => {
                // Check if this member is selected
                const isSelected =
                  member.id && selectedHosts.includes(member.id);

                return (
                  <CommandItem
                    key={member.id}
                    value={member.email}
                    onSelect={() => {
                      if (member.id) {
                        console.log('Toggling host:', member.id, member.email);
                        toggleHost(member.id);
                        if (
                          eventCategory === 'ONE_ON_ONE' ||
                          eventCategory === 'GROUP'
                        ) {
                          setOpenHostsSelector(false); // Close dropdown after selection for single host types
                        }
                      }
                    }}
                  >
                    <div className="flex items-center w-full">
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="flex-1 truncate">{member.email}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedHosts.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedHosts.map((hostId) => (
            <Badge key={hostId} variant="secondary" className="gap-1">
              {getMemberNameById(hostId)}
              <button
                onClick={() => toggleHost(hostId)}
                className="hover:bg-muted ml-1 rounded-full"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
